import omit from 'lodash/omit';

import { queryKeyPrefix } from '@src/corelib/query/utils';
import { useMutation } from '@src/query';

import usePartChannelPostMutation from '../inventory/usePartChannelPostMutation';


/**
 * Listings tracker mutation hooks (T10). Each wraps the shared `useMutation`
 * engine with the injected-`queryClient` `mutateFn({ apiClient, queryClient })`
 * contract — the corelib query singleton is NOT imported here. Invalidation runs
 * inside `mutateFn` (the one place `queryClient` is injected) so the exact
 * tracker cache key prefix `['root','','inventory','listings',sellerId]` is
 * preserved across the channel toggle and the bulk mark action.
 *
 * Mirrors `query/garage/useDonorMutations.js`: one
 * `query/<area>/use<Thing>Mutations.js` module re-exported through the
 * `@src/query` barrel.
 */

/** Cache key prefix for a seller's listings tracker, shared across the page. */
function listingsKeyPrefix(sellerId) {
  return queryKeyPrefix('inventory', 'listings', String(sellerId || ''));
}


/**
 * Toggle a single part's per-channel listing state (Plane #103/#155). Variables:
 * `{ sellerId, partId, channel, live }`. POSTs to the part channels endpoint and
 * invalidates the tracker so the toggle reflects the new state. Also backs the
 * per-channel mark-listed action from the copy modal.
 */
function useListingChannelToggleMutation(params) {
  return usePartChannelPostMutation({
    buildPayload: (vars) => omit(vars, ['apiClient', 'queryClient', 'sellerId', 'partId']),
    invalidate: ({ queryClient, sellerId }) => queryClient
      .invalidateQueries({ queryKey: listingsKeyPrefix(sellerId) }),
  }, params);
}


/**
 * Bulk mark-listed / mark-removed on a channel over the cross-page selection
 * (Plane #105). Variables: `{ sellerId, action, ids, channel }`. Hits the shared
 * parts bulk endpoint — same contract as Inventory's bulk bar — then invalidates
 * the tracker so toggles reflect the new state.
 */
function useListingBulkMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, ...payload }) => {
      const data = await apiClient.request({
        url: `/inventory/parts/${sellerId}/bulk/`,
        method: 'POST',
        payload,
      });
      queryClient.invalidateQueries({ queryKey: listingsKeyPrefix(sellerId) });
      return data;
    },
    ...params,
  });
}


export {
  listingsKeyPrefix,
  useListingBulkMutation,
  useListingChannelToggleMutation,
};
